import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Skeleton,
  SkeletonItem,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  AddRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import { saveValueMeasures } from "apps/project-iq/api";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { IAdminRowAction } from "components/adminList/AdminListPage.types";
import { IValueMeasure } from "models/IProjectIQWorkspace";
import { ITableColumn } from "models/ITableColumn";
import { useAdminListSort } from "hooks/useAdminListSort";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  markProjectIqWorkspaceUpdated,
  selectAvailableProjectIqWorkspaces,
  updateProjectIqWorkspace as updateWorkspaceAction,
} from "redux/projectIqWorkspacesSlice";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { RootState } from "store";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useLinks } from "apps/project-iq/hooks/useLinks";
import {
  IValueMeasureDraft,
  IValuePageProps,
  ValueSaveStatus,
} from "./ValuePage.types";
import { useValuePageStyles } from "./ValuePage.styles";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";

const PAGE_DESCRIPTION =
  "Document the expected business value and outcomes for this engagement. Align on value hypotheses, key value drivers, and success metrics to guide delivery and demonstrate impact.";

export const ValuePage = ({
  workspaceIdentifier,
  onSaveStatusChange,
  isAddMeasureOpen,
  onAddMeasureClose,
}: IValuePageProps): JSX.Element => {
  const styles = useValuePageStyles();
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const colStyles = useAdminColumnStyles();
  const { links: valueLinks, isLoading: isLinksLoading } = useLinks("value");
  const dispatch = useDispatch();
  const workspaces = useSelector(selectAvailableProjectIqWorkspaces);
  const workspace = workspaces.find((w) => w.id === workspaceIdentifier);
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const canEdit =
    workspace?.owners?.some((owner) => owner.userId === currentUser?.id) ??
    false;
  const lastFocusRef = useRef<HTMLElement | null>(null);

  const [valueMeasures, setValueMeasures] = useState<IValueMeasure[]>([]);
  const [saveStatus, setSaveStatus] = useState<ValueSaveStatus>("idle");
  const [isAddEditDialogOpen, setIsAddEditDialogOpen] = useState(false);
  const [draft, setDraft] = useState<IValueMeasureDraft | null>(null);
  const [draftMeasureError, setDraftMeasureError] = useState("");
  const [deletingMeasureIdentifier, setDeletingMeasureIdentifier] = useState<
    string | null
  >(null);

  useEffect(() => {
    onSaveStatusChange?.(saveStatus);
  }, [saveStatus, onSaveStatusChange]);

  useEffect(() => {
    setValueMeasures(workspace?.valueMeasures ?? []);
  }, [workspace?.id]);

  const save = useCallback(
    async (measures: IValueMeasure[]) => {
      if (!workspace) return;
      setSaveStatus("saving");
      try {
        const updated = await saveValueMeasures({
          id: workspaceIdentifier,
          valueMeasures: measures,
        });
        dispatch(
          updateWorkspaceAction({
            ...workspace,
            valueMeasures: updated.valueMeasures,
          }),
        );
        dispatch(markProjectIqWorkspaceUpdated(workspace.id));
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch {
        setSaveStatus("error");
      }
    },
    [workspaceIdentifier, workspace, dispatch],
  );

  const handleOpenAddDialog = useCallback(() => {
    lastFocusRef.current = document.activeElement as HTMLElement;
    setDraft({
      id: crypto.randomUUID(),
      valueMeasure: "",
      type: "",
      description: "",
    });
    setDraftMeasureError("");
    setIsAddEditDialogOpen(true);
  }, []);

  useEffect(() => {
    if (isAddMeasureOpen) {
      handleOpenAddDialog();
      onAddMeasureClose?.();
    }
  }, [isAddMeasureOpen, handleOpenAddDialog, onAddMeasureClose]);

  const handleOpenEditDialog = useCallback((measure: IValueMeasure) => {
    lastFocusRef.current = document.activeElement as HTMLElement;
    setDraft({
      id: measure.id,
      valueMeasure: measure.valueMeasure,
      type: measure.type ?? "",
      description: measure.description ?? "",
    });
    setDraftMeasureError("");
    setIsAddEditDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsAddEditDialogOpen(false);
    setDraft(null);
    setDraftMeasureError("");
    requestAnimationFrame(() => lastFocusRef.current?.focus());
  }, []);

  const handleDraftChange = useCallback(
    (field: keyof IValueMeasureDraft, value: string) => {
      setDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const handleSaveMeasure = useCallback(() => {
    if (!draft) return;
    if (!draft.valueMeasure.trim()) {
      setDraftMeasureError("Value measure is required");
      return;
    }
    const entry: IValueMeasure = {
      id: draft.id,
      valueMeasure: draft.valueMeasure.trim(),
      type: draft.type.trim() || undefined,
      description: draft.description.trim() || undefined,
    };
    const next = valueMeasures.some((m) => m.id === entry.id)
      ? valueMeasures.map((m) => (m.id === entry.id ? entry : m))
      : [...valueMeasures, entry];
    setValueMeasures(next);
    void save(next);
    handleCloseDialog();
  }, [draft, valueMeasures, save, handleCloseDialog]);

  const handleDeleteMeasure = useCallback((identifier: string) => {
    lastFocusRef.current = document.activeElement as HTMLElement;
    setDeletingMeasureIdentifier(identifier);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deletingMeasureIdentifier) return;
    const next = valueMeasures.filter(
      (m) => m.id !== deletingMeasureIdentifier,
    );
    setValueMeasures(next);
    setDeletingMeasureIdentifier(null);
    void save(next);
    requestAnimationFrame(() => lastFocusRef.current?.focus());
  }, [deletingMeasureIdentifier, valueMeasures, save]);

  const handleCancelDelete = useCallback(() => {
    setDeletingMeasureIdentifier(null);
    requestAnimationFrame(() => lastFocusRef.current?.focus());
  }, []);

  const deletingMeasure = useMemo(
    () => valueMeasures.find((m) => m.id === deletingMeasureIdentifier),
    [valueMeasures, deletingMeasureIdentifier],
  );

  const isEditing = useMemo(
    () => draft !== null && valueMeasures.some((m) => m.id === draft.id),
    [draft, valueMeasures],
  );

  const isSaveDisabled = !draft?.valueMeasure?.trim();

  const {
    sortColumn,
    isSortDescending,
    onSortColumnChanged,
    onClearColumnChanged,
    sortItems,
  } = useAdminListSort<IValueMeasure>({ cacheKey: "ValueMeasures" });

  const columns: ITableColumn<IValueMeasure>[] = useMemo(
    () => [
      {
        columnId: "valueMeasure",
        displayValue: "Value Measure",
        className: colStyles.normal,
        compare: (a, b) => a.valueMeasure.localeCompare(b.valueMeasure),
        renderHeaderCell: () => "Value Measure",
        renderCell: (item) => item.valueMeasure,
      },
      {
        columnId: "type",
        displayValue: "Type",
        className: colStyles.normal,
        compare: (a, b) => (a.type ?? "").localeCompare(b.type ?? ""),
        renderHeaderCell: () => "Type",
        renderCell: (item) => item.type ?? "—",
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        compare: (a, b) =>
          (a.description ?? "").localeCompare(b.description ?? ""),
        renderHeaderCell: () => "Description",
        renderCell: (item) => item.description ?? "—",
      },
    ],
    [],
  );

  const sortedMeasures = useMemo(
    () => sortItems(valueMeasures, columns),
    [sortItems, valueMeasures, columns],
  );

  const rowActions: IAdminRowAction<IValueMeasure>[] = useMemo(() => {
    if (!canEdit) return [];
    return [
      {
        icon: <EditRegular />,
        tooltip: "Edit",
        onClick: handleOpenEditDialog,
      },
      {
        icon: <DeleteRegular />,
        tooltip: "Delete",
        onClick: (measure) => handleDeleteMeasure(measure.id),
      },
    ];
  }, [canEdit, handleOpenEditDialog, handleDeleteMeasure]);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <div>
          <p className={styles.description}>{PAGE_DESCRIPTION}</p>
        </div>

        {isLinksLoading ? (
          <Skeleton>
            <div className={styles.skeletonLinksBar}>
              <SkeletonItem
                animation="wave"
                className={styles.skeletonLink}
                style={{ width: "38%" }}
              />
              <SkeletonItem
                animation="wave"
                className={styles.skeletonLink}
                style={{ width: "22%" }}
              />
              <SkeletonItem
                animation="wave"
                className={styles.skeletonLink}
                style={{ width: "52%" }}
              />
            </div>
          </Skeleton>
        ) : valueLinks.length > 0 ? (
          <div className={styles.linksBar}>
            {valueLinks.map((link) => (
              <a
                key={link.id}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.externalLink}
              >
                {link.displayName} →
              </a>
            ))}
          </div>
        ) : null}

        <Button
          appearance="primary"
          className={styles.addNewButton}
          icon={<AddRegular aria-hidden="true" />}
          onClick={handleOpenAddDialog}
          disabled={!canEdit}
        >
          Add New
        </Button>

        <div className={styles.adminListWrapper}>
          <AdminListPage<IValueMeasure>
            items={sortedMeasures}
            columns={columns}
            getRowId={(m) => m.id}
            rowActions={rowActions}
            emptyStateMessage="No value measures yet. Click Add measure to get started."
            sortColumn={sortColumn}
            isSortDescending={isSortDescending}
            onSortChange={onSortColumnChanged}
            onSortClear={onClearColumnChanged}
            actionsWidth="96px"
            hideHeader
          />
        </div>
      </div>

      <Dialog
        open={isAddEditDialogOpen}
        onOpenChange={(_, data) => {
          if (!data.open) handleCloseDialog();
        }}
      >
        <DialogSurface className={contentStyles.dialogSurface}>
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<DismissRegular />}
                  aria-label="Close"
                  onClick={handleCloseDialog}
                />
              }
            >
              {isEditing ? "Edit value measure" : "Add value measure"}
            </DialogTitle>
            <DialogContent className={contentStyles.tabbedDialogContent}>
              <div className={contentStyles.formContainer}>
                <Field
                  label="Value Measure"
                  required
                  validationState={draftMeasureError ? "error" : "none"}
                  validationMessage={draftMeasureError || undefined}
                >
                  <Input
                    value={draft?.valueMeasure ?? ""}
                    onChange={(_e, d) => {
                      handleDraftChange("valueMeasure", d.value);
                      if (draftMeasureError) setDraftMeasureError("");
                    }}
                    placeholder="e.g. Reduce time to hire"
                  />
                </Field>
                <Field
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      Type
                      <Tooltip
                        content="e.g. financial, culture"
                        relationship="description"
                      >
                        <span role="img" aria-label="More information">
                          <InfoRegular
                            style={{ fontSize: "14px" }}
                            aria-hidden="true"
                          />
                        </span>
                      </Tooltip>
                    </span>
                  }
                >
                  <Input
                    value={draft?.type ?? ""}
                    onChange={(_e, d) => handleDraftChange("type", d.value)}
                    placeholder="e.g. financial"
                  />
                </Field>
                <Field label="Description">
                  <Textarea
                    value={draft?.description ?? ""}
                    onChange={(_e, d) =>
                      handleDraftChange("description", d.value)
                    }
                    resize="vertical"
                    rows={3}
                    placeholder="Optional description"
                  />
                </Field>
              </div>
            </DialogContent>
          </DialogBody>
          <div className={footerStyles.dialogFooter}>
            <DialogFooterAction
              icon={<CheckmarkRegular />}
              tooltip="Save"
              ariaLabel={
                isSaveDisabled
                  ? "Save value measure (value measure is required)"
                  : "Save value measure"
              }
              dimmed={isSaveDisabled}
              onClick={handleSaveMeasure}
            />
          </div>
        </DialogSurface>
      </Dialog>

      <ActionDialog
        isVisible={deletingMeasureIdentifier !== null}
        title="Delete value measure?"
        message={`Are you sure you want to delete "${deletingMeasure?.valueMeasure || "this measure"}"? This cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleConfirmDelete}
        onDismiss={handleCancelDelete}
      />
    </div>
  );
};
